import Route from 'cio/routes/base';
import { inject as service } from '@ember/service';
import DS from 'ember-data';
import { hash } from 'rsvp';
import AnalyticsService from 'cio/services/analytics';
import ApiCredentialsController from 'cio/pods/settings/api-credentials/controller';
import { Crumb } from 'cio/services/crumbs';
import Transition from '@ember/routing/-private/transition';
import { redirectAwayAndAlert, redirectIfNoJourneysAccess, redirectIfViewer } from 'cio/utils/route-access';
import isDestroyed from 'cio/utils/is-destroyed';
import { SETTING_LINKS_ALL } from '../index/controller';
import AuthService from 'cio/services/auth';

export default class ApiCredentialsRoute extends Route {
  @service analytics!: AnalyticsService;
  @service store!: DS.Store;
  @service auth!: AuthService;

  crumb(): Crumb {
    return {
      name: 'API Credentials',
      path: this.routeName,
    };
  }

  async beforeModel(transition: Transition) {
    await super.beforeModel(transition);

    if (SETTING_LINKS_ALL.apiCredentials.restrictedToProducts != null && this.account.isPipelinesOnlyPlan) {
      return redirectIfNoJourneysAccess(this, transition, 'apiCredentials');
    }

    if (!this.auth.hasPermission('account/api_credentials/view')) {
      return redirectAwayAndAlert(this, transition, {
        redirectTo: 'settings.index',
        alertMessage: "You do not have permission to view API credentials. If you'd like access, let an Admin know.",
      });
    }

    return redirectIfViewer(this, transition);
  }

  model() {
    let { store } = this;

    return hash({
      appKeys: store.findAll('ext-api-key'),
      whitelistedIpAddresses: store.findAll('whitelisted-ip'),
      environments: store.peekAll('environment'),
    });
  }

  setupController(controller: ApiCredentialsController, model: any, transition: Transition) {
    super.setupController(controller, model, transition);

    controller.fetchApiCredentialsTask.perform();

    if (controller.keyType === 'app') {
      this.analytics.track('integration', 'app_api_keys_viewed');
    } else if (controller.keyType === 'tracking') {
      this.analytics.track('integration', 'tracking_api_keys_viewed');
    }
  }

  deactivate() {
    super.deactivate();

    if (isDestroyed(this.store)) {
      return;
    }

    [
      ...[...(this.controller.model as Record<string, any>).appKeys],
      ...[...(this.controller.model as Record<string, any>).whitelistedIpAddresses],
      ...((this.controller as ApiCredentialsController).credentials ?? []),
    ].forEach((key) => {
      if (isDestroyed(key)) {
        return;
      }

      return key.isNew ? key.rollbackAttributes() : key;
    });
  }
}
